import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Clock, CheckCircle, XCircle, Truck, Package, ChefHat, AlertCircle, Calendar, ChevronDown, ChevronUp, RotateCcw, Download, Printer, Minus, Plus } from 'lucide-react';
import {
  MesCommandesProductReferenceRow,
  MesCommandesRdvReferenceRow,
  MesCommandesProductTableHead,
  MesCommandesRdvTableHead,
} from '../components/mesCommandes/MesCommandesReferenceRows';
import {
  MES_COMMANDES_PLANNING_PATH,
  MES_COMMANDES_PLANNING_LINK_LABEL,
  MES_COMMANDES_PRESTATIONS_RDV_BLOCK_TITLE,
  MES_COMMANDES_QUICK_VIEW,
  filterCommandesByQuickView,
  mesCommandesSubtitleForCapabilities,
  normalizeMesCommandesQuickView,
  resolveMesCommandesMerchantCapabilities,
  shouldShowMesCommandesProductBlock,
  shouldShowMesCommandesRdvBlock,
  formatRdvDureeLabel,
  getCommandeProductLines,
  getMultiProductExtraCount,
  getPrestationAccentColor,
  isMesCommandesActionRequise,
  resolveCommandeProductThumbUrl,
  resolvePrestationThumbUrl,
  buildMesCommandesRdvEnCoursFooterState,
  MES_COMMANDES_RDV_REDUCE_LIST_LABEL,
  resolveMesCommandesRdvEnCoursDisplayed,
} from '../utils/mesCommandesMixedUx';
import MesCommandesMaquetteToolbar from '../components/mesCommandes/MesCommandesMaquetteToolbar';
import MesCommandesMaquetteBlock, {
  MesCommandesMaquetteEmptyRow,
} from '../components/mesCommandes/MesCommandesMaquetteBlock';
import { MES_COMMANDES_MAQUETTE_TABLE_CLASS } from '../components/mesCommandes/mesCommandesMaquetteTableLayout';
import apiService from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import {
  isCommandeEnCours,
  isCommandeLivreurModifiable,
  formatLivreurNom,
  isMerchantCommandeEnCours,
  isMerchantCommandeTerminee,
  merchantExpeditionStatusLabel,
} from '../utils/commandeStatuts';
import { resolveCommandeDetailTotals } from '../utils/commandeDetailTotals';
import {
  resolveLignePrixPromo,
  estimateIndisponibleRefundDelta,
} from '../utils/commandeLignePrixPromo';
import { isFactureTelechargeable } from '../utils/commandeFactureDisponible';
import { buildReductionDisplayLines } from '../utils/commandePricingDisplay';
import { getRdvPaymentView, formatEuro, canRefundRendezVous, getRdvSoldeRemboursable } from '../utils/rdvPaymentDisplay';
import { getRdvModalTitle, getRdvListPrestationLabel, getRdvNumeroDisplay } from '../utils/rdvStatusUx';
import RdvTerminerModal from '../components/rendezVous/RdvTerminerModal';
import { createCaisseIdempotencyKey } from '../utils/caisseIdempotency';
import {
  buildTenderBreakdown,
  hasGiftTender,
  isOnlinePaidPriceLocked,
  isOnlinePaidPriceLockedForMutation,
  isVariableWeightLine,
  commandeHasVariableWeightLines,
  PRICE_LOCK_MESSAGE,
  VARIABLE_WEIGHT_LOCK_EXCEPTION_HINT,
} from '../utils/paiementTenderBreakdown';
import { estimerPrixDepuisPoids } from '../utils/variableWeightEstimate';
import { buildModifiedLignesPoidsReel } from '../utils/variableWeightAdjustmentPayload';
import {
  getCommandeFinancier,
  getListPrincipalAmount,
  getPendingComplementCheckoutUrl,
} from '../utils/commandeFinanceDisplay';
import {
  resolveLivraisonAdminBadgeMeta,
  resolveLivraisonCommandeDisplay,
} from '../utils/livraisonCommandeDisplay';
import ProBadge from '../components/b2b/ProBadge';
import {
  canShowGenerateExpeditionButton,
  expeditionShippingOrderErrorMessage,
} from '../utils/expeditionShippingOrderUi';
import {
  canShowExpeditionLabelActions,
  expeditionLabelStatusMessage,
} from '../utils/expeditionShippingLabelUi';
import { startExpeditionLabelPolling as startLabelStatusPoller } from '../utils/expeditionShippingLabelPolling';
import {
  canShowExpeditionMerchantHandoffButton,
  expeditionMerchantHandoffConfirmCopy,
} from '../utils/expeditionMerchantHandoffUi';
import {
  canShowExpeditionTrackingPanel,
  canSyncExpeditionTracking,
  expeditionCarrierFollowLinkLabel,
  expeditionDraftPreparationMessage,
  expeditionShipmentStatusLabel,
} from '../utils/expeditionShipmentTrackingUi';
import CommandeLigneDetails from '../components/commande/CommandeLigneDetails';
import { getCommandeLigneDesignation } from '../utils/commandeLigneDetailViewModel';
import { buildMesCommandesRowContext } from '../utils/mesCommandesRowContextBuilder';
import MesCommandesListPaiementCell from '../components/mesCommandes/MesCommandesListPaiementCell';
import MesCommandesRdvDetailModal from '../components/mesCommandes/MesCommandesRdvDetailModal';
import MesCommandesOrderDetailModal from '../components/mesCommandes/MesCommandesOrderDetailModal';

const REPRINT_ERROR_MESSAGES = {
  NO_PRINTER: 'Aucune imprimante configurée',
  CONFIG_INCOMPLETE: 'Configuration imprimante incomplète',
  TOGGLE_FILTERED: 'Impression refusée',
  PRINT_FAILED: 'Erreur d\'impression',
};

const CommercantCommandes = ({ commercantId, initialCommandeId = null, onCommandeDetailClosed, onDataChanged }) => {
  const { toasts, toast, removeToast } = useToast();
  const [commandes, setCommandes] = useState([]);
  const commandesRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [terminerModal, setTerminerModal] = useState({ open: false, commande: null });
  const [encaisserSoldeUi, setEncaisserSoldeUi] = useState({
    loading: false,
    methode: 'CARTE',
    hasSession: false,
    probing: false,
    error: '',
  });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    statut: '',
    dateMin: '',
    dateMax: ''
  });
  const [quickViewFilter, setQuickViewFilter] = useState(MES_COMMANDES_QUICK_VIEW.ALL);
  const [rdvEnCoursListExpanded, setRdvEnCoursListExpanded] = useState(false);
  const [merchantModules, setMerchantModules] = useState({ loaded: false, moduleProduits: true, modulePrestations: false });
  const [sectionsExpanded, setSectionsExpanded] = useState({
    enCours: true, // Toujours ouvert
    terminees: false,
    annulees: false
  });
  const [livreursDisponibles, setLivreursDisponibles] = useState([]);
  const [expeditionCapabilities, setExpeditionCapabilities] = useState({
    boxtalShippingOrderEnabled: false,
  });
  const [generatingShippingOrder, setGeneratingShippingOrder] = useState(false);
  const [merchantHandoffSubmitting, setMerchantHandoffSubmitting] = useState(false);
  const [expeditionTrackingSyncing, setExpeditionTrackingSyncing] = useState(false);
  const [expeditionLabelPolling, setExpeditionLabelPolling] = useState(false);
  const labelPollStopRef = useRef(null);
  const [expeditionLabelUi, setExpeditionLabelUi] = useState({
    loading: false,
    downloading: false,
    status: null,
    format: null,
  });
  const [livreurHistorique, setLivreurHistorique] = useState(null);
  const [selectedLivreurId, setSelectedLivreurId] = useState('');
  const [assigningLivreur, setAssigningLivreur] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundType, setRefundType] = useState('ARTICLES');
  const [refundMontant, setRefundMontant] = useState('');
  const [refundMotif, setRefundMotif] = useState('');
  const [refundLineQty, setRefundLineQty] = useState({});
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [factureLoading, setFactureLoading] = useState(false);
  const [avoirLoadingId, setAvoirLoadingId] = useState(null);
  const [poidsDrafts, setPoidsDrafts] = useState({});
  const [savingPoids, setSavingPoids] = useState(false);
  const [poidsMessage, setPoidsMessage] = useState('');
  const [poidsComplementUrl, setPoidsComplementUrl] = useState('');
  const [poidsError, setPoidsError] = useState('');
  const [markingIndisponible, setMarkingIndisponible] = useState(false);
  const [indisponibleMessage, setIndisponibleMessage] = useState('');
  const [reprintLoading, setReprintLoading] = useState(false);
  const filtersInitializedRef = useRef(false);
  const isRendezVous = (commande) => commande?.type === 'RENDEZ_VOUS';

  const STATUTS_LIGNE_INDISPONIBLE = [
    'EN_ATTENTE',
    'CONFIRMEE',
    'EN_PREPARATION',
    'PRETE',
    'EN_LIVRAISON',
  ];

  const getLigneDesignation = (item) => getCommandeLigneDesignation(item);

  const getLigneIndisponibleErrorMessage = (err) => {
    const code = err?.data?.error?.code || err?.code;
    switch (code) {
      case 'LIGNE_DEJA_INDISPONIBLE':
        return 'Cet article est déjà marqué indisponible. Le détail a été actualisé.';
      case 'DERNIERE_LIGNE_FACTURABLE':
        return "Impossible de retirer le dernier article facturable. Pour annuler toute la commande, utilisez l'annulation complète.";
      case 'STATUT_NON_AUTORISE':
        return 'Cette commande ne peut plus être modifiée à ce statut.';
      case 'FORBIDDEN':
        return 'Accès refusé pour cette commande.';
      case 'LIGNE_NOT_FOUND':
      case 'COMMANDE_NOT_FOUND':
        return 'Commande ou ligne introuvable.';
      default:
        return (
          err?.data?.error?.message ||
          err?.data?.message ||
          err?.message ||
          "Erreur lors du marquage de l'article indisponible"
        );
    }
  };

  const isCommandePaiementEnLigne = (cmd) =>
    cmd?.modePaiement === 'enligne' ||
    ['paypal', 'applepay', 'googlepay'].includes(String(cmd?.modePaiement || '').toLowerCase());

  const getCommandeSoldeRemboursable = (cmd) => {
    const paiements = cmd?.paiements || [];
    const encaisse = paiements
      .filter((p) => p.statutPaiement === 'VALIDE' && Number(p.montant) > 0)
      .reduce((s, p) => s + Number(p.montant), 0);
    const rembourse = Math.abs(
      paiements
        .filter((p) => p.statutPaiement === 'REMBOURSE' && Number(p.montant) < 0)
        .reduce((s, p) => s + Number(p.montant), 0)
    );
    return Math.round((encaisse - rembourse) * 100) / 100;
  };

  const hasStripePaymentIntent = (cmd) =>
    (cmd?.paiements || []).some(
      (p) =>
        p.statutPaiement === 'VALIDE' &&
        Number(p.montant) > 0 &&
        p.providerTransactionId &&
        String(p.providerTransactionId).startsWith('pi_')
    );

  const parseSavReference = (reference) => {
    if (!reference) return null;
    if (typeof reference === 'object') return reference;
    try {
      return JSON.parse(reference);
    } catch {
      return null;
    }
  };

  const getRefundedQtyByLigne = (cmd) => {
    const map = {};
    (cmd?.paiements || []).forEach((p) => {
      if (p.statutPaiement !== 'REMBOURSE' || Number(p.montant) >= 0) return;
      const meta = parseSavReference(p.reference);
      (meta?.ligneIds || []).forEach((lid) => {
        const q = meta?.ligneQuantites?.[lid];
        map[lid] = (map[lid] || 0) + (q ? Number(q) : Number.POSITIVE_INFINITY);
      });
    });
    return map;
  };

  const getLigneUnitPrice = (ligne) => {
    const qte = Math.max(1, parseInt(ligne?.quantite, 10) || 1);
    const total = Number(ligne?.prixFinal ?? ligne?.prixTotal ?? (ligne?.prixUnitaire || 0) * qte);
    return total / qte;
  };

  const getLigneRemainingQty = (ligne, cmd) => {
    if (ligne?.indisponibleAt != null) return 0;
    const qte = Math.max(0, parseInt(ligne?.quantite, 10) || 0);
    const refunded = getRefundedQtyByLigne(cmd)[ligne.id];
    if (refunded === Number.POSITIVE_INFINITY) return 0;
    return Math.max(0, qte - (refunded || 0));
  };

  const isPaiementStripe = (cmd) =>
    isCommandePaiementEnLigne(cmd) || cmd?.methodePaiement === 'CARTE';

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setShowRefundModal(false);
    setShowRefundConfirm(false);
    setRefundError('');
    setReprintLoading(false);
    onCommandeDetailClosed?.();
  };

  const handleReprintTicket = async () => {
    if (!selectedCommande?.id || reprintLoading) return;

    setReprintLoading(true);
    try {
      const result = await apiService.reprintTicket(selectedCommande.id);
      if (result?.printed || result?.success) {
        toast.success(result?.message || 'Ticket envoyé à l\'imprimante');
        return;
      }
      const reason = result?.reason;
      toast.error(
        result?.message ||
          REPRINT_ERROR_MESSAGES[reason] ||
          result?.error ||
          'Erreur d\'impression',
      );
    } catch (err) {
      const payload = err?.data || err?.response?.data || {};
      const reason = payload?.reason;
      toast.error(
        payload?.message ||
          REPRINT_ERROR_MESSAGES[reason] ||
          err?.message ||
          'Erreur d\'impression',
      );
    } finally {
      setReprintLoading(false);
    }
  };

  const analyzeFreeAmountVatUi = (cmd) => {
    const products = (cmd?.produits || []).filter((p) => !p.indisponibleAt);
    if (!products.length) {
      return { freeAmountAllowed: false, isMultiVat: false, incomplete: true };
    }
    const rates = [];
    let incomplete = false;
    for (const p of products) {
      const taux = p.tauxTVASnapshot;
      const hasTtc = p.montantTTCSnapshot != null && Number.isFinite(Number(p.montantTTCSnapshot));
      if (taux == null || !Number.isFinite(Number(taux)) || !hasTtc) {
        incomplete = true;
        continue;
      }
      rates.push(Number(taux));
    }
    const unique = [...new Set(rates)];
    if (incomplete || unique.length === 0) {
      return { freeAmountAllowed: false, isMultiVat: false, incomplete: true };
    }
    if (unique.length > 1) {
      return { freeAmountAllowed: false, isMultiVat: true, incomplete: false };
    }
    return { freeAmountAllowed: true, isMultiVat: false, incomplete: false, monoTaux: unique[0] };
  };

  const openRefundModal = (commande) => {
    const gift = hasGiftTender(commande || selectedCommande);
    setRefundType(gift ? 'COMPLET' : 'ARTICLES');
    setRefundMontant('');
    setRefundMotif('');
    setRefundLineQty({});
    setRefundError('');
    setShowRefundModal(true);
  };

  const openRefundConfirm = () => {
    if (!isRendezVous(selectedCommande) && hasGiftTender(selectedCommande) && refundType !== 'COMPLET') {
      setRefundError(
        'Le remboursement partiel n’est pas disponible pour une commande payée (même partiellement) en carte cadeau. Effectuez un remboursement total, qui sera réparti automatiquement sur les moyens d’origine.'
      );
      return;
    }
    if (refundType === 'ARTICLES') {
      if (!Object.values(refundLineQty).some((q) => q > 0)) {
        setRefundError('Sélectionnez au moins un article');
        return;
      }
      const selectedTotal = (selectedCommande?.produits || []).reduce((s, lp) => {
        const qty = refundLineQty[lp.id] || 0;
        if (qty <= 0) return s;
        return s + getLigneUnitPrice(lp) * qty;
      }, 0);
      const solde = getCommandeSoldeRemboursable(selectedCommande);
      if (selectedTotal > solde + 0.001) {
        setRefundError(`Montant supérieur au solde remboursable (${formatEuro(solde)})`);
        return;
      }
    }
    if (refundType === 'LIBRE') {
      if (!isRendezVous(selectedCommande)) {
        const freeVat = analyzeFreeAmountVatUi(selectedCommande);
        if (!freeVat.freeAmountAllowed) {
          setRefundError(
            freeVat.isMultiVat
              ? 'Cette commande comporte plusieurs taux de TVA. Pour un remboursement partiel, sélectionnez les articles concernés.'
              : 'Montant libre indisponible : ventilation TVA incomplète.'
          );
          return;
        }
      }
      const montant = parseFloat(refundMontant, 10);
      if (!selectedCommande || !montant || montant <= 0) {
        setRefundError('Montant invalide');
        return;
      }
      const solde = isRendezVous(selectedCommande)
        ? getRdvSoldeRemboursable(selectedCommande)
        : getCommandeSoldeRemboursable(selectedCommande);
      if (montant > solde + 0.001) {
        setRefundError(`Montant supérieur au solde remboursable (${formatEuro(solde)})`);
        return;
      }
    }
    setRefundError('');
    setShowRefundConfirm(true);
  };

  const handleDownloadFacture = async () => {
    if (!selectedCommande) return;
    setFactureLoading(true);
    try {
      await apiService.downloadCommandeFacture(selectedCommande.id);
    } catch (e) {
      setError(e.message || 'Impossible de télécharger la facture');
    } finally {
      setFactureLoading(false);
    }
  };

  const handleDownloadAvoir = async (avoirId) => {
    if (!selectedCommande?.id || !avoirId) return;
    setAvoirLoadingId(avoirId);
    try {
      await apiService.downloadCommandeAvoir(selectedCommande.id, avoirId);
    } catch (e) {
      setError(e.message || "Impossible de télécharger l'avoir");
    } finally {
      setAvoirLoadingId(null);
    }
  };

  const handleRembourserCommande = async () => {
    if (!selectedCommande) return false;
    setRefundLoading(true);
    setRefundError('');
    try {
      let payload;
      if (refundType === 'COMPLET') {
        payload = { type: 'COMPLET', motif: refundMotif || undefined };
      } else if (refundType === 'ARTICLES') {
        const ligneIds = Object.keys(refundLineQty).filter((id) => refundLineQty[id] > 0);
        const ligneQuantites = {};
        let montant = 0;
        ligneIds.forEach((id) => {
          const ligne = (selectedCommande.produits || []).find((lp) => lp.id === id);
          const qty = refundLineQty[id];
          ligneQuantites[id] = qty;
          if (ligne) montant += getLigneUnitPrice(ligne) * qty;
        });
        payload = {
          type: 'LIBRE',
          montant: Math.round(montant * 100) / 100,
          motif: refundMotif || undefined,
          ligneIds,
          ligneQuantites,
          annulerCommande: false,
        };
      } else {
        payload = {
          type: 'LIBRE',
          montant: parseFloat(refundMontant, 10),
          motif: refundMotif || undefined,
        };
      }
      const isRdv = isRendezVous(selectedCommande);
      const result = isRdv
        ? await apiService.rembourserRendezVous(selectedCommande.id, payload)
        : await apiService.rembourserCommande(selectedCommande.id, payload);
      if (isRdv) {
        await fetchCommandesSilent();
        try {
          const data = await apiService.getCommande(selectedCommande.id);
          const full = data?.commande || data;
          if (full?.id) setSelectedCommande(full);
        } catch {
          if (result?.snapshot) {
            setSelectedCommande((prev) =>
              prev
                ? {
                    ...prev,
                    amountPaid: result.snapshot.amountPaid,
                    amountDueLater: result.snapshot.amountDueLater,
                    paymentStatus: result.snapshot.paymentStatus,
                    paymentQuote: {
                      ...(prev.paymentQuote || {}),
                      ...result.snapshot,
                    },
                    paiementIntentStatut: result.snapshot.paiementIntentStatut,
                  }
                : prev
            );
          }
        }
      } else {
        const updated = result.commande || result;
        setSelectedCommande(updated);
        await fetchCommandesSilent();
        try {
          const data = await apiService.getCommande(updated.id);
          const full = data?.commande || data;
          if (full?.id) setSelectedCommande(full);
        } catch {
          /* garde la réponse remboursement */
        }
      }
      setShowRefundModal(false);
      setShowRefundConfirm(false);
      await onDataChanged?.();
      return true;
    } catch (e) {
      setRefundError(e.message || 'Erreur lors du remboursement');
      setShowRefundConfirm(false);
      return false;
    } finally {
      setRefundLoading(false);
    }
  };

  const getRdvParticipants = (commande) => Math.max(1, Number(commande?.participants ?? 1));

  const getRdvTotalAmount = (commande) =>
    Number(commande?.prixFinal ?? commande?.total ?? commande?.totalTTC ?? 0);

  const getRdvUnitPrice = (commande) => {
    const participants = getRdvParticipants(commande);
    const total = getRdvTotalAmount(commande);
    if (!participants || participants <= 0) {
      return total;
    }
    return total / participants;
  };

  const RDV_STATUS_CONFIG = {
    EN_ATTENTE: {
      label: 'En attente',
      color: 'bg-yellow-100 text-yellow-800',
      icon: <Clock className="h-4 w-4" />,
    },
    CONFIRME: {
      label: 'Confirmé',
      color: 'bg-blue-100 text-blue-800',
      icon: <CheckCircle className="h-4 w-4" />,
    },
    TERMINE: {
      label: 'Terminé',
      color: 'bg-green-100 text-green-800',
      icon: <CheckCircle className="h-4 w-4" />,
    },
    ABSENT: {
      label: 'Client absent',
      color: 'bg-orange-100 text-orange-800',
      icon: <AlertCircle className="h-4 w-4" />,
    },
    ANNULE: {
      label: 'Annulé',
      color: 'bg-red-100 text-red-800',
      icon: <XCircle className="h-4 w-4" />,
    },
  };

  const getRdvStatusDisplay = (statut) =>
    RDV_STATUS_CONFIG[statut] || {
      label: statut,
      color: 'bg-gray-100 text-gray-800',
      icon: <Clock className="h-4 w-4" />,
    };

  const getStatusDisplay = (commande) => {
    if (isRendezVous(commande)) {
      return getRdvStatusDisplay(commande.statut);
    }

    const isPosComptoir = commande.canalVente === 'POS' && commande.modeRetrait === 'comptoir';
    let label =
      merchantExpeditionStatusLabel(commande) ||
      getStatusLabel(commande.statut, commande.modeRetrait, commande);
    if (isPosComptoir && commande.statut === 'LIVREE') {
      label = 'TERMINÉE';
    }

    return {
      label,
      color: getStatusColor(commande.statut),
      icon: getStatusIcon(commande.statut),
    };
  };

  const RDV_ACTIONS = {
    EN_ATTENTE: [
      { status: 'CONFIRME', label: 'Confirmer', tone: 'primary' },
      { status: 'ANNULE', label: 'Annuler', tone: 'danger' },
    ],
    CONFIRME: [
      { status: 'TERMINE', label: 'Terminer', tone: 'primary' },
      { status: 'ABSENT', label: 'Client absent', tone: 'primary' },
      { status: 'ANNULE', label: 'Annuler', tone: 'danger' },
    ],
    TERMINE: [],
    ABSENT: [],
    ANNULE: [],
  };

  const getRdvActions = (statut) => RDV_ACTIONS[statut] || [];

  const formatDateTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeBadge = (commande) => {
    if (isRendezVous(commande)) {
      return {
        label: 'Rendez-vous',
        className: 'bg-purple-100 text-purple-800',
        icon: <Calendar className="h-3 w-3" />,
      };
    }

    if (commande.modeRetrait === 'livraison') {
      const livraisonBadge = resolveLivraisonAdminBadgeMeta(commande);
      return {
        label: livraisonBadge?.label || 'Livraison',
        className: livraisonBadge?.className || 'bg-green-100 text-green-800',
        icon: livraisonBadge?.kind === 'programmee'
          ? <Clock className="h-3 w-3" />
          : <Truck className="h-3 w-3" />,
        kind: livraisonBadge?.kind || 'asap',
        timingLines: livraisonBadge?.timingLines || ['Dès que possible'],
      };
    }

    if (commande.modeRetrait === 'expedition') {
      return {
        label: 'Expédition',
        className: 'bg-indigo-100 text-indigo-800',
        icon: <Package className="h-3 w-3" />,
      };
    }

    if (commande.modeRetrait === 'clickandcollect') {
      return {
        label: 'Click & Collect',
        className: 'bg-blue-100 text-blue-800',
        icon: <Package className="h-3 w-3" />,
      };
    }

    return {
      label: 'Commande',
      className: 'bg-gray-100 text-gray-700',
      icon: <ChefHat className="h-3 w-3" />,
    };
  };

  const getRdvActionIcon = (status) => {
    switch (status) {
      case 'CONFIRME':
        return { icon: <CheckCircle className="h-5 w-5" />, className: 'text-blue-600 hover:text-blue-900' };
      case 'TERMINE':
        return { icon: <CheckCircle className="h-5 w-5" />, className: 'text-green-600 hover:text-green-900' };
      case 'ABSENT':
        return { icon: <AlertCircle className="h-5 w-5" />, className: 'text-orange-600 hover:text-orange-900' };
      case 'ANNULE':
        return { icon: <XCircle className="h-5 w-5" />, className: 'text-red-600 hover:text-red-900' };
      default:
        return { icon: <Clock className="h-5 w-5" />, className: 'text-gray-500 hover:text-gray-700' };
    }
  };

  const getRdvActionButtonClass = (status) => {
    switch (status) {
      case 'CONFIRME':
        return 'bg-blue-600 text-white hover:bg-blue-700';
      case 'TERMINE':
        return 'bg-green-600 text-white hover:bg-green-700';
      case 'ABSENT':
        return 'bg-orange-100 text-orange-700 hover:bg-orange-200';
      case 'ANNULE':
        return 'bg-red-600 text-white hover:bg-red-700';
      default:
        return 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    }
  };

  const getRdvConfirmationMeta = (commande, targetStatus) => {
    const numero = commande.numero || `RDV-${String(commande.id || '').toUpperCase()}`;
    const when = commande.dateDebut ? formatDateTime(commande.dateDebut) : null;

    switch (targetStatus) {
      case 'CONFIRME':
        return {
          title: 'Confirmer le rendez-vous',
          description: when
            ? `Confirmer le rendez-vous ${numero} prévu le ${when} ?`
            : `Confirmer le rendez-vous ${numero} ?`,
          tone: 'primary',
        };
      case 'TERMINE':
        return {
          title: 'Terminer le rendez-vous',
          description: 'Marquer ce rendez-vous comme terminé ?',
          tone: 'primary',
        };
      case 'ABSENT':
        return {
          title: 'Client absent',
          description: 'Marquer ce rendez-vous comme client absent ?',
          tone: 'primary',
        };
      case 'ANNULE':
        return {
          title: 'Annuler le rendez-vous',
          description: when
            ? `Annuler le rendez-vous ${numero} prévu le ${when} ?`
            : `Annuler le rendez-vous ${numero} ?`,
          tone: 'danger',
        };
      default:
        return {
          title: 'Mettre à jour le rendez-vous',
          description: `Confirmer le changement de statut (${targetStatus}) pour ${numero} ?`,
          tone: 'primary',
        };
    }
  };

  useEffect(() => {
    commandesRef.current = commandes;
  }, [commandes]);

  const mesCommandesCapabilities = useMemo(
    () =>
      resolveMesCommandesMerchantCapabilities(
        {
          moduleProduits: merchantModules.moduleProduits,
          modulePrestations: merchantModules.modulePrestations,
        },
        { loaded: merchantModules.loaded },
      ),
    [merchantModules],
  );

  useEffect(() => {
    if (!commercantId || !apiService.isAuthenticated()) {
      setMerchantModules({ loaded: false, moduleProduits: true, modulePrestations: false });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getCommercant(commercantId);
        if (!cancelled && data) {
          setMerchantModules({
            loaded: true,
            moduleProduits: data.moduleProduits ?? true,
            modulePrestations: data.modulePrestations ?? false,
          });
        }
      } catch {
        if (!cancelled) {
          setMerchantModules({ loaded: true, moduleProduits: true, modulePrestations: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commercantId]);

  useEffect(() => {
    if (!mesCommandesCapabilities.loaded) return;
    setQuickViewFilter((prev) => normalizeMesCommandesQuickView(prev, mesCommandesCapabilities));
  }, [mesCommandesCapabilities.loaded, mesCommandesCapabilities.productsEnabled, mesCommandesCapabilities.servicesEnabled]);

  useEffect(() => {
    setRdvEnCoursListExpanded(false);
  }, [quickViewFilter, commercantId]);

  useEffect(() => {
    if (!commercantId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let retryTimer;

    const load = async () => {
      if (!apiService.isAuthenticated()) {
        return;
      }
      await fetchCommandes();
    };

    load();

    const intervalId = setInterval(() => {
      fetchCommandesSilent();
    }, 5000);

    const scheduleRetry = (delayMs) => {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(async () => {
        if (cancelled) return;
        if (commandesRef.current.length > 0) return;
        if (!apiService.isAuthenticated()) {
          scheduleRetry(Math.min(delayMs + 1000, 5000));
          return;
        }
        await fetchCommandesSilent();
        if (!cancelled && commandesRef.current.length === 0) {
          scheduleRetry(Math.min(delayMs * 2, 8000));
        }
      }, delayMs);
    };
    scheduleRetry(1200);

    const onRealtimeStatut = () => {
      fetchCommandesSilent();
    };
    window.addEventListener('clickorico:refresh-commandes-list', onRealtimeStatut);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      clearTimeout(retryTimer);
      window.removeEventListener('clickorico:refresh-commandes-list', onRealtimeStatut);
    };
  }, [commercantId]);

  useEffect(() => {
    if (!commercantId || !apiService.isAuthenticated()) {
      setExpeditionCapabilities({ boxtalShippingOrderEnabled: false });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getExpeditionCapabilities(commercantId);
        if (!cancelled) {
          setExpeditionCapabilities({
            boxtalShippingOrderEnabled: data?.boxtalShippingOrderEnabled === true,
          });
        }
      } catch {
        if (!cancelled) {
          setExpeditionCapabilities({ boxtalShippingOrderEnabled: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commercantId]);

  useEffect(() => {
    if (!initialCommandeId || loading) return;

    const openDetail = async () => {
      const found = commandes.find((c) => c.id === initialCommandeId);
      if (found) {
        setSelectedCommande(found);
        setShowDetailModal(true);
        return;
      }
      try {
        const data = await apiService.getCommande(initialCommandeId);
        const cmd = data?.commande || data;
        if (cmd?.id) {
          setSelectedCommande(cmd);
          setShowDetailModal(true);
        }
      } catch (err) {
        console.error('Impossible de charger le détail commande:', err);
      }
    };

    openDetail();
  }, [initialCommandeId, commandes, loading]);

  const fetchCommandesSilent = async () => {
    if (!commercantId) {
      setLoading(false);
      return;
    }
    if (!apiService.isAuthenticated()) {
      return;
    }

    try {
      const query = buildQueryParams({ pageSize: 200 });
      const data = await apiService.getCommandes(query);
      const commandesCommercant = (data.data || []).filter(
        (commande) =>
          commande.commercantId === commercantId ||
          commande.commercant?.id === commercantId
      );
      setCommandes(commandesCommercant);
      setError('');
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err?.message === 'NOT_AUTHENTICATED') {
        setError('Session expirée. Veuillez vous reconnecter.');
        setCommandes([]);
      } else {
        console.error('Erreur auto-refresh commandes commerçant:', err);
      }
    }
  };

  const buildQueryParams = (base = {}) => {
    const params = { ...base, commercantId, excludeCanalVente: 'POS' };
    if (filters.search.trim()) {
      params.search = filters.search.trim();
    }
    if (filters.statut) {
      params.statut = filters.statut;
    }
    if (filters.dateMin) {
      params.dateMin = filters.dateMin;
    }
    if (filters.dateMax) {
      params.dateMax = filters.dateMax;
    }
    return params;
  };

  const fetchCommandes = async () => {
    if (!commercantId) {
      setLoading(false);
      return;
    }
    if (!apiService.isAuthenticated()) {
      setCommandes([]);
      setError('Session expirée. Veuillez vous reconnecter.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const query = buildQueryParams({ pageSize: 200 });
      const data = await apiService.getCommandes(query);
      const commandesCommercant = (data.data || []).filter(
        (commande) =>
          commande.commercantId === commercantId ||
          commande.commercant?.id === commercantId
      );
      setCommandes(commandesCommercant);
      setError('');
    } catch (err) {
      if (err?.message === 'NOT_AUTHENTICATED') {
        setError('Session expirée. Veuillez vous reconnecter.');
        setCommandes([]);
      } else {
        console.error('Commandes commerçant error:', err);
        setCommandes((prev) => {
          if (prev.length === 0) {
            setError('Erreur lors du chargement des commandes');
          }
          return prev;
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!commercantId) return;
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true;
      return;
    }
    fetchCommandes();
  }, [filters, commercantId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => {
        const trimmed = searchInput.trim();
        if (prev.search === trimmed) {
          return prev;
        }
        return { ...prev, search: trimmed };
      });
    }, 300);

    return () => clearTimeout(handler);
  }, [searchInput]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      statut: '',
      dateMin: '',
      dateMax: ''
    });
  };

  const resolveCommercantId = (commande) =>
    commande?.commercantId || commande?.commercant?.id || commercantId || null;

  const normalizeLivreursList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.livreurs)) return data.livreurs;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  const refreshCommandeDetail = useCallback(async (commandeId) => {
    try {
      const data = await apiService.getCommande(commandeId);
      const full = data?.commande || data;
      if (!full?.id) return null;
      setSelectedCommande((prev) => (prev?.id === full.id ? { ...prev, ...full } : prev));
      setCommandes((prev) => prev.map((c) => (c.id === full.id ? { ...c, ...full } : c)));
      return full;
    } catch (err) {
      console.error('Erreur rechargement détail commande:', err);
      return null;
    }
  }, []);

  const applyCommandeStatePatch = useCallback((commandeId, patch) => {
    if (!commandeId || !patch) return;
    setSelectedCommande((prev) =>
      prev && prev.id === commandeId ? { ...prev, ...patch } : prev
    );
    setCommandes((prev) =>
      prev.map((c) => (c.id === commandeId ? { ...c, ...patch } : c))
    );
  }, []);

  const normalizeAssignementResult = (result) => {
    const commande = result?.commande;
    if (!commande?.id) return null;
    const livreur = commande.livreur || result?.livreur || null;
    return {
      ...commande,
      livreurId: commande.livreurId || livreur?.id || null,
      livreur,
    };
  };

  const loadLivreursForCommande = async (commande) => {
    const targetCommercantId = resolveCommercantId(commande);
    if (!targetCommercantId) return;
    try {
      const data = await apiService.getLivreurs(targetCommercantId);
      const list = normalizeLivreursList(data).filter((l) => l.actif !== false);
      setLivreursDisponibles(list);
      setSelectedLivreurId(commande?.livreurId || commande?.livreur?.id || '');
    } catch (err) {
      console.error('Erreur chargement livreurs:', err);
      setLivreursDisponibles([]);
    }
  };

  const handleAssignerLivreur = async (force = false) => {
    if (!selectedCommande || !selectedLivreurId) return;
    if (!isCommandeLivreurModifiable(selectedCommande.statut)) {
      alert('Cette commande est terminée ou annulée : le livreur ne peut plus être modifié.');
      return;
    }
    try {
      setAssigningLivreur(true);
      const result = await apiService.assignerLivreur(selectedCommande.id, {
        livreurId: selectedLivreurId,
        force,
      });
      const merged = normalizeAssignementResult(result);
      if (merged) {
        applyCommandeStatePatch(selectedCommande.id, merged);
        setSelectedLivreurId(merged.livreurId || '');
        await loadLivreursForCommande(merged);
      }
      await refreshCommandeDetail(selectedCommande.id);
      await fetchCommandesSilent();
      window.dispatchEvent(new CustomEvent('clickorico:refresh-commandes-pending-count'));
    } catch (err) {
      const msg = err?.message || 'Erreur lors de l\'assignation du livreur';
      alert(msg);
    } finally {
      setAssigningLivreur(false);
    }
  };

  useEffect(() => {
    if (!showDetailModal || !selectedCommande?.id) {
      setLivreurHistorique(null);
      return;
    }
    if (selectedCommande.modeRetrait !== 'livraison' || isRendezVous(selectedCommande)) {
      setLivreurHistorique(null);
      return;
    }
    refreshCommandeDetail(selectedCommande.id);
  }, [showDetailModal, selectedCommande?.id, refreshCommandeDetail]);

  useEffect(() => {
    if (!showDetailModal || !selectedCommande?.id) {
      setLivreurHistorique(null);
      return;
    }
    const livreurId = selectedCommande.livreurId || selectedCommande.livreur?.id;
    if (!livreurId || selectedCommande.livreur) {
      setLivreurHistorique(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getLivreur(livreurId);
        const livreur = data?.livreur || data;
        if (!cancelled && livreur?.id) {
          setLivreurHistorique(livreur);
        }
      } catch (err) {
        console.error('Erreur chargement livreur (historique):', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDetailModal, selectedCommande?.id, selectedCommande?.livreurId, selectedCommande?.livreur]);

  useEffect(() => {
    if (!showDetailModal || !selectedCommande) return;
    if (
      selectedCommande.modeRetrait === 'livraison' &&
      !isRendezVous(selectedCommande) &&
      isCommandeLivreurModifiable(selectedCommande.statut)
    ) {
      loadLivreursForCommande(selectedCommande);
    } else {
      setLivreursDisponibles([]);
      setSelectedLivreurId('');
    }
  }, [showDetailModal, selectedCommande?.id, selectedCommande?.modeRetrait, selectedCommande?.livreurId, selectedCommande?.statut]);

  useEffect(() => {
    if (!selectedCommande?.produits) {
      setPoidsDrafts({});
      return;
    }
    const next = {};
    selectedCommande.produits.forEach((item) => {
      if (!isVariableWeightLine(item)) return;
      const poidsReel = item.poidsReel ?? item.poidsSouhaite ?? '';
      const estim = estimerPrixDepuisPoids(item, poidsReel);
      next[item.id] = {
        poidsReel,
        prixEstime:
          estim != null
            ? estim
            : item.prixFinal ?? item.prixTotal ?? '',
      };
    });
    setPoidsDrafts(next);
    setPoidsMessage('');
    setPoidsComplementUrl('');
    setPoidsError('');
  }, [selectedCommande?.id, selectedCommande?.produits]);

  const handleSavePoidsAjustement = async () => {
    if (!selectedCommande?.id) return;
    if (
      isOnlinePaidPriceLockedForMutation(selectedCommande, {
        mutation: 'variable_weight_adjust',
      })
    ) {
      setPoidsError(PRICE_LOCK_MESSAGE);
      return;
    }
    const lignes = buildModifiedLignesPoidsReel(
      poidsDrafts,
      selectedCommande?.produits,
    );
    if (lignes.length === 0) {
      setPoidsError(
        'Aucune modification de poids réel par rapport aux montants enregistrés',
      );
      return;
    }
    try {
      setSavingPoids(true);
      setPoidsError('');
      setPoidsMessage('');
      const result = await apiService.ajusterPoidsLignesCommande(
        selectedCommande.id,
        lignes,
      );
      if (result?.commande) {
        setSelectedCommande(result.commande);
      } else {
        await refreshCommandeDetail(selectedCommande.id);
      }
      await fetchCommandesSilent();
      const delta = Number(result?.delta) || 0;
      const paiement = result?.paiement || {};
      let msg = `Total commande : ${formatEuro(Number(result?.nouveauTotal || 0))}`;
      if (paiement.netCollected != null) {
        msg += ` · Net encaissé : ${formatEuro(Number(paiement.netCollected))}`;
      }
      if (paiement.action === 'refund' && paiement.refund?.amount) {
        msg += ` · Remboursement Stripe ${formatEuro(Number(paiement.refund.amount))}`;
        setPoidsComplementUrl('');
      } else if (paiement.action === 'complement' && paiement.complement?.amount) {
        msg += ` · Reste à encaisser : ${formatEuro(Number(paiement.complement.amount))}`;
        if (paiement.complement?.checkoutUrl) {
          setPoidsComplementUrl(paiement.complement.checkoutUrl);
        }
      } else if (paiement.action === 'complement_failed') {
        msg += ` · Complément ${formatEuro(Number(paiement.complement?.amount || delta))} à encaisser (Stripe KO)`;
      } else if (delta !== 0) {
        msg += ` · Écart ${delta > 0 ? '+' : ''}${formatEuro(delta)}`;
      } else {
        setPoidsComplementUrl('');
      }
      setPoidsMessage(msg);
    } catch (err) {
      setPoidsError(
        err?.data?.message ||
          err?.data?.error ||
          err?.message ||
          'Erreur lors de l’ajustement poids',
      );
    } finally {
      setSavingPoids(false);
    }
  };

  const openLigneIndisponibleConfirmation = (item) => {
    if (!selectedCommande?.id || !item?.id) return;
    if (isOnlinePaidPriceLocked(selectedCommande)) {
      toast.error(PRICE_LOCK_MESSAGE);
      return;
    }
    const designation = getLigneDesignation(item);
    const lignes = selectedCommande.produits || [];
    const sousTotalOriginal =
      Math.round(
        lignes.reduce(
          (s, p) => s + (Number(p.prixFinal ?? p.prixTotal) || 0),
          0
        ) * 100
      ) / 100;
    const prixAffiche = resolveLignePrixPromo(item, {
      reductionOrig: selectedCommande.reduction,
      sousTotalOriginal,
    });
    const estimate = estimateIndisponibleRefundDelta(
      selectedCommande,
      item.id
    );
    const willAutoRefund =
      isCommandePaiementEnLigne(selectedCommande) &&
      selectedCommande.statutPaiement === 'VALIDE' &&
      estimate.refundDelta >= 0.01;
    const prixLabel = prixAffiche.hasPromo
      ? `${formatEuro(prixAffiche.brut)} → ${formatEuro(prixAffiche.net)} promo`
      : `${formatEuro(prixAffiche.brut)}`;
    let description = `Confirmer que « ${designation} » (${prixLabel}) est indisponible ? Le total passera de ${formatEuro(estimate.ancienTotal)} à ${formatEuro(estimate.nouveauTotal)}. Les frais de livraison restent inchangés ; la réduction est recalculée sur le panier restant.`;
    if (willAutoRefund) {
      description += ` Un remboursement Stripe de ${formatEuro(estimate.refundDelta)} (écart de total) sera déclenché automatiquement.`;
    }
    setConfirmation({
      kind: 'ligne-indisponible',
      commandeId: selectedCommande.id,
      ligneId: item.id,
      title: 'Article indisponible',
      description,
      tone: 'danger',
      confirmLabel: willAutoRefund
        ? 'Marquer et rembourser'
        : 'Marquer indisponible',
    });
  };

  const handleMarquerLigneIndisponible = async (commandeId, ligneId) => {
    if (!commandeId || !ligneId || markingIndisponible) return false;
    try {
      setMarkingIndisponible(true);
      setIndisponibleMessage('');
      const result = await apiService.marquerLigneIndisponible(
        commandeId,
        ligneId
      );
      await refreshCommandeDetail(commandeId);
      await fetchCommandesSilent();
      window.dispatchEvent(
        new CustomEvent('clickorico:refresh-commandes-pending-count')
      );
      const paiement = result?.paiement || {};
      let msg = `Total mis à jour : ${formatEuro(Number(result?.nouveauTotal || 0))}`;
      if (paiement.action === 'refund' && paiement.refund?.amount) {
        msg += ` · Remboursement Stripe ${formatEuro(Number(paiement.refund.amount))}`;
      } else if (paiement.action === 'refund_failed') {
        msg += ` · Remboursement Stripe échoué — total déjà recalculé (${paiement.refund?.error || 'erreur'})`;
      } else if (
        Number(result?.delta) < -0.005 &&
        paiement.action === 'local_total_only'
      ) {
        msg += ` · Écart ${formatEuro(Number(result.delta))} (pas de remboursement auto)`;
      }
      setIndisponibleMessage(msg);
      return true;
    } catch (err) {
      const code = err?.data?.error?.code || err?.code;
      if (code === 'LIGNE_DEJA_INDISPONIBLE' && commandeId) {
        await refreshCommandeDetail(commandeId);
      }
      alert(getLigneIndisponibleErrorMessage(err));
      console.error('Erreur ligne indisponible:', err);
      return false;
    } finally {
      setMarkingIndisponible(false);
    }
  };

  const updateCommandeStatus = async (commandeId, newStatus) => {
    try {
      setUpdatingStatusId(commandeId);
      await apiService.updateCommande(commandeId, { statut: newStatus });
      await fetchCommandes();
      if (showDetailModal && selectedCommande?.id === commandeId) {
        await refreshCommandeDetail(commandeId);
      } else {
        setSelectedCommande((prev) =>
          prev && prev.id === commandeId ? { ...prev, statut: newStatus } : prev
        );
      }
      window.dispatchEvent(new CustomEvent('clickorico:refresh-commandes-pending-count'));
    } catch (err) {
      console.error('Update status error:', err);
      throw err;
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const updateRendezVousStatus = async (commandeId, newStatus, extra = {}) => {
    try {
      setUpdatingStatusId(commandeId);
      await apiService.updateRendezVous(commandeId, { statut: newStatus, ...extra });
      await fetchCommandes();
      setSelectedCommande((prev) =>
        prev && prev.id === commandeId ? { ...prev, statut: newStatus } : prev
      );
    } catch (err) {
      alert('Erreur lors de la mise à jour du rendez-vous');
      console.error('Update rendez-vous status error:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  /** Gift-only + digital — pas mode digital seul (aligné backend isGiftOnlyDigitalOrder). */
  const isGiftOnlyDigitalCommande = (commande) => {
    if (!commande || String(commande.modeRetrait || '').toLowerCase() !== 'digital') {
      return false;
    }
    const lines = Array.isArray(commande.produits) ? commande.produits : [];
    if (lines.length === 0) {
      // Listes sans lignes : digital reste contractuellement Gift-only à la création V1.
      return true;
    }
    return lines.every(
      (l) =>
        l?.giftCardAmountCents != null ||
        Boolean(l?.giftCardTemplateId) ||
        l?.produit?.typeProduit === 'GIFT_CARD' ||
        l?.typeProduit === 'GIFT_CARD'
    );
  };

  const getNextStatus = (currentStatus, modeRetrait, commande = null) => {
    // Carte cadeau numérique gift-only : déjà finalisée — aucun clic commerçant
    if (isGiftOnlyDigitalCommande(commande || { modeRetrait })) return null;

    const isClickCollect = modeRetrait === 'clickandcollect';
    const isExpedition = modeRetrait === 'expedition';
    // Expédition / C&C : pas d'EN_LIVRAISON (pas de livreur local)
    const skipEnLivraison = isClickCollect || isExpedition;

    switch (currentStatus) {
      case 'EN_ATTENTE':
      case 'CONFIRMEE':
        return 'EN_PREPARATION';
      case 'EN_PREPARATION':
        return skipEnLivraison ? 'PRETE' : 'EN_LIVRAISON';
      case 'PRETE':
        if (isExpedition) return null;
        return skipEnLivraison ? 'LIVREE' : 'EN_LIVRAISON';
      case 'EN_LIVRAISON':
        return 'LIVREE';
      default:
        return null;
    }
  };

  const getNextStatusLabel = (currentStatus, modeRetrait, commande = null) => {
    if (isGiftOnlyDigitalCommande(commande || { modeRetrait })) return null;

    const isClickCollect = modeRetrait === 'clickandcollect';
    const isExpedition = modeRetrait === 'expedition';

    const labels = {
      EN_ATTENTE: 'Confirmer',
      CONFIRMEE: 'Mettre en préparation',
      EN_PREPARATION: isClickCollect
        ? 'Marquer comme prête au retrait'
        : isExpedition
          ? 'Marquer comme prête'
          : 'Passer en livraison',
      PRETE: isClickCollect
        ? 'Marquer comme retirée'
        : isExpedition
          ? null
          : 'Passer en livraison',
      EN_LIVRAISON: 'Marquer comme livrée',
    };

    return labels[currentStatus] || null;
  };

  const getStatusLabel = (statut, modeRetrait, commande = null) => {
    if (isGiftOnlyDigitalCommande(commande || { modeRetrait })) {
      switch (statut) {
        case 'LIVREE':
          return 'CARTE CADEAU ÉMISE';
        case 'ANNULEE':
          return 'ANNULÉE';
        default:
          return 'CARTE CADEAU';
      }
    }

    const isClickCollect = modeRetrait === 'clickandcollect';
    const isExpedition = modeRetrait === 'expedition';

    switch (statut) {
      case 'EN_ATTENTE':
        return 'EN ATTENTE';
      case 'CONFIRMEE':
        return 'CONFIRMÉE';
      case 'EN_PREPARATION':
        return 'EN PRÉPARATION';
      case 'PRETE':
        return isClickCollect ? 'PRÊTE AU RETRAIT' : 'PRÊTE';
      case 'EN_LIVRAISON':
        return isClickCollect ? 'PRÊTE AU RETRAIT' : 'EN LIVRAISON';
      case 'LIVREE':
        return isClickCollect ? 'RETIRÉE' : isExpedition ? 'EXPÉDIÉE' : 'LIVRÉE';
      case 'ANNULEE':
        return 'ANNULÉE';
      default:
        return statut;
    }
  };

  const openStatusConfirmation = (commande, targetStatus, options = {}) => {
    if (!targetStatus) {
      console.warn('openStatusConfirmation: targetStatus is missing', { commande, targetStatus });
      return;
    }

    const label = getStatusLabel(targetStatus, commande.modeRetrait, commande);
    const numero = commande.numero || `#${String(commande.id || '').toUpperCase()}`;

    const isLivraison = commande?.modeRetrait === 'livraison' && !isRendezVous(commande);
    const alreadyAssigned = !!(commande?.livreurId || commande?.livreur?.id);
    const needsAssignPrompt = targetStatus === 'EN_LIVRAISON' && isLivraison && !alreadyAssigned;

    const preloadLivreursIfNeeded = async () => {
      if (!needsAssignPrompt) {
        setLivreursDisponibles([]);
        setSelectedLivreurId('');
        return { hasLivreurs: false };
      }
      const targetCommercantId = resolveCommercantId(commande);
      if (!targetCommercantId) {
        setLivreursDisponibles([]);
        setSelectedLivreurId('');
        return { hasLivreurs: false };
      }
      try {
        const data = await apiService.getLivreurs(targetCommercantId);
        const list = normalizeLivreursList(data).filter((l) => l.actif !== false);
        setLivreursDisponibles(list);
        setSelectedLivreurId('');
        return { hasLivreurs: list.length > 0 };
      } catch (err) {
        console.error('Erreur chargement livreurs (actions rapides):', err);
        setLivreursDisponibles([]);
        setSelectedLivreurId('');
        return { hasLivreurs: false };
      }
    };

    (async () => {
      const { hasLivreurs } = await preloadLivreursIfNeeded();
      const requireAssign = needsAssignPrompt && hasLivreurs;

      setConfirmation({
        kind: 'commande-status',
        commandeId: commande.id,
        targetStatus,
        requireAssign,
        options,
        title: targetStatus === 'ANNULEE' ? 'Annuler la commande' : 'Changer le statut',
        description: `Confirmer le passage de la commande ${numero} vers le statut « ${label} » ?`,
        tone: targetStatus === 'ANNULEE' ? 'danger' : 'primary',
        confirmLabel: requireAssign ? 'Passer en livraison' : 'Confirmer',
        showAssignSelect: requireAssign,
      });
    })();
  };

  const stopExpeditionLabelPolling = useCallback(() => {
    if (labelPollStopRef.current) {
      labelPollStopRef.current();
      labelPollStopRef.current = null;
    }
    setExpeditionLabelPolling(false);
  }, []);

  const applyExpeditionLabelApiData = useCallback((data) => {
    setExpeditionLabelUi({
      loading: false,
      downloading: false,
      status: data?.status || null,
      format: data?.format || null,
    });
    if (data?.shipment?.id) {
      setSelectedCommande((prev) =>
        prev && prev.shipment?.id === data.shipment.id
          ? {
              ...prev,
              shipment: { ...prev.shipment, ...data.shipment },
            }
          : prev,
      );
    }
  }, []);

  const refreshExpeditionLabelStatus = useCallback(
    async (shipmentId, { silent = false } = {}) => {
      if (!shipmentId || !commercantId) return null;
      setExpeditionLabelUi((prev) => ({ ...prev, loading: true }));
      try {
        const data = await apiService.getExpeditionShippingLabelStatus(shipmentId, commercantId);
        applyExpeditionLabelApiData(data);
        return data;
      } catch (err) {
        setExpeditionLabelUi((prev) => ({
          ...prev,
          loading: false,
          status: null,
          format: null,
        }));
        if (!silent) {
          toast.error(err?.message || 'Impossible de vérifier l’étiquette');
        }
        return null;
      }
    },
    [commercantId, applyExpeditionLabelApiData, toast],
  );

  const startExpeditionLabelPolling = useCallback(
    (shipmentId) => {
      if (!shipmentId || !commercantId) return;
      stopExpeditionLabelPolling();
      setExpeditionLabelPolling(true);
      const poller = startLabelStatusPoller({
        tick: async () => {
          const data = await apiService.getExpeditionShippingLabelStatus(shipmentId, commercantId);
          applyExpeditionLabelApiData(data);
          if (data?.status === 'available') {
            return { status: 'available' };
          }
          if (data?.status === 'pending') {
            return { status: 'pending' };
          }
          return { status: 'error', terminal: true };
        },
        onSettled: () => {
          labelPollStopRef.current = null;
          setExpeditionLabelPolling(false);
        },
      });
      labelPollStopRef.current = poller.stop;
    },
    [commercantId, stopExpeditionLabelPolling, applyExpeditionLabelApiData],
  );

  const handleSyncExpeditionTracking = async () => {
    const shipmentId = selectedCommande?.shipment?.id;
    const commandeId = selectedCommande?.id;
    if (!shipmentId || !commercantId || !commandeId || expeditionTrackingSyncing) return;
    if (!canSyncExpeditionTracking(selectedCommande)) return;
    setExpeditionTrackingSyncing(true);
    try {
      await apiService.syncExpeditionShipmentTracking(shipmentId, commercantId);
      await refreshCommandeDetail(commandeId);
      toast.success('Suivi actualisé');
    } catch (err) {
      toast.error(
        err?.data?.detail || err?.data?.message || err?.message || 'Impossible d’actualiser le suivi',
      );
    } finally {
      setExpeditionTrackingSyncing(false);
    }
  };

  const handleDownloadExpeditionLabel = async (shipmentId, commandeId) => {
    if (!shipmentId || !commercantId || expeditionLabelUi.downloading) return;
    try {
      setExpeditionLabelUi((prev) => ({ ...prev, downloading: true }));
      await apiService.downloadExpeditionShippingLabel(shipmentId, commercantId);
      await refreshExpeditionLabelStatus(shipmentId, { silent: true });
      await refreshCommandeDetail(commandeId);
      toast.success('Étiquette téléchargée');
    } catch (err) {
      if (err?.code === 'LABEL_PENDING') {
        setExpeditionLabelUi((prev) => ({ ...prev, downloading: false, status: 'pending' }));
        toast.info('Étiquette en cours de génération');
        return;
      }
      toast.error(err?.message || 'Téléchargement de l’étiquette impossible');
    } finally {
      setExpeditionLabelUi((prev) => ({ ...prev, downloading: false }));
    }
  };

  useEffect(() => {
    return () => {
      stopExpeditionLabelPolling();
    };
  }, [stopExpeditionLabelPolling]);

  useEffect(() => {
    if (!showDetailModal) {
      stopExpeditionLabelPolling();
    }
  }, [showDetailModal, stopExpeditionLabelPolling]);

  useEffect(() => {
    stopExpeditionLabelPolling();
  }, [selectedCommande?.id, stopExpeditionLabelPolling]);

  useEffect(() => {
    const shipment = selectedCommande?.shipment;
    if (!showDetailModal || !canShowExpeditionLabelActions(selectedCommande)) {
      stopExpeditionLabelPolling();
      setExpeditionLabelUi({
        loading: false,
        downloading: false,
        status: null,
        format: null,
      });
      return;
    }
    refreshExpeditionLabelStatus(shipment.id, { silent: true });
  }, [
    showDetailModal,
    selectedCommande?.id,
    selectedCommande?.shipment?.id,
    selectedCommande?.shipment?.providerShipmentId,
    refreshExpeditionLabelStatus,
    stopExpeditionLabelPolling,
  ]);

  const openGenerateExpeditionConfirmation = () => {
    if (!selectedCommande?.shipment?.id || generatingShippingOrder) return;
    setConfirmation({
      kind: 'expedition-shipping-order',
      shipmentId: selectedCommande.shipment.id,
      commandeId: selectedCommande.id,
      title: 'Générer l’expédition',
      description: 'Générer l’expédition auprès du transporteur ?',
      tone: 'primary',
      confirmLabel: 'Générer',
    });
  };

  const openExpeditionHandoffConfirmation = () => {
    if (!selectedCommande?.shipment?.id || merchantHandoffSubmitting) return;
    if (!canShowExpeditionMerchantHandoffButton(selectedCommande)) return;
    const copy = expeditionMerchantHandoffConfirmCopy();
    setConfirmation({
      kind: 'expedition-handoff',
      shipmentId: selectedCommande.shipment.id,
      commandeId: selectedCommande.id,
      title: copy.title,
      description: copy.description,
      tone: 'primary',
      confirmLabel: copy.confirmLabel,
    });
  };

  const handleMerchantExpeditionHandoff = async (shipmentId, commandeId) => {
    if (!shipmentId || !commercantId || merchantHandoffSubmitting) return false;
    try {
      setMerchantHandoffSubmitting(true);
      await apiService.handoffExpeditionShipment(shipmentId, commercantId);
      await refreshCommandeDetail(commandeId);
      await fetchCommandesSilent();
      toast.success('Colis enregistré comme remis au transporteur');
      return true;
    } catch (err) {
      toast.error(
        err?.message ||
          err?.response?.data?.message ||
          err?.data?.detail ||
          'Impossible d’enregistrer la remise au transporteur',
      );
      return false;
    } finally {
      setMerchantHandoffSubmitting(false);
    }
  };

  const handleGenerateExpeditionShippingOrder = async (shipmentId, commandeId) => {
    if (!shipmentId || !commercantId || generatingShippingOrder) return false;
    try {
      setGeneratingShippingOrder(true);
      const result = await apiService.createBoxtalShippingOrder(shipmentId, commercantId);
      if (result?.shipment) {
        setSelectedCommande((prev) =>
          prev && prev.id === commandeId
            ? {
                ...prev,
                shipment: { ...prev.shipment, ...result.shipment },
              }
            : prev,
        );
      }
      await refreshCommandeDetail(commandeId);
      await fetchCommandesSilent();
      const labelData = await refreshExpeditionLabelStatus(shipmentId, { silent: true });
      if (labelData?.status === 'pending') {
        startExpeditionLabelPolling(shipmentId);
      } else {
        stopExpeditionLabelPolling();
      }
      if (result?.outcome === 'already_created' || result?.outcome === 'created') {
        toast.success('Expédition transporteur créée');
      }
      return true;
    } catch (err) {
      const code = err?.code || err?.error || err?.response?.data?.code;
      const specific = expeditionShippingOrderErrorMessage(code);
      toast.error(
        specific ||
          err?.message ||
          err?.response?.data?.message ||
          'Impossible de créer l’expédition transporteur',
      );
      return false;
    } finally {
      setGeneratingShippingOrder(false);
    }
  };

  const handleConfirmationConfirm = async () => {
    if (!confirmation) return false;

    try {
      if (confirmation.kind === 'expedition-shipping-order') {
        return handleGenerateExpeditionShippingOrder(
          confirmation.shipmentId,
          confirmation.commandeId,
        );
      }

      if (confirmation.kind === 'expedition-handoff') {
        return handleMerchantExpeditionHandoff(
          confirmation.shipmentId,
          confirmation.commandeId,
        );
      }

      if (confirmation.kind === 'ligne-indisponible') {
        const { commandeId, ligneId } = confirmation;
        return handleMarquerLigneIndisponible(commandeId, ligneId);
      }

      if (confirmation.kind === 'commande-status') {
        const { commandeId, targetStatus, requireAssign, options } = confirmation;

        if (requireAssign) {
          if (!selectedLivreurId) return false;
          const result = await apiService.assignerLivreur(commandeId, { livreurId: selectedLivreurId });
          const merged = normalizeAssignementResult(result);
          if (merged) {
            applyCommandeStatePatch(commandeId, merged);
            setSelectedLivreurId(merged.livreurId || '');
            await loadLivreursForCommande(merged);
          }
        }

        await updateCommandeStatus(commandeId, targetStatus);

        if (options?.afterConfirm) {
          await options.afterConfirm();
        } else if (showDetailModal) {
          closeDetailModal();
        }
      } else if (confirmation.kind === 'rdv-status') {
        const { commandeId, targetStatus, payload, afterConfirm } = confirmation;
        await updateRendezVousStatus(commandeId, targetStatus, payload || {});
        if (afterConfirm) {
          await afterConfirm();
        }
      }

      return true;
    } catch (error) {
      console.error('Erreur dans handleConfirmationConfirm:', error);
      if (confirmation.kind === 'commande-status') {
        try {
          const data = await apiService.getCommande(confirmation.commandeId);
          const cmd = data?.commande || data;
          if (cmd?.statut === confirmation.targetStatus) {
            applyCommandeStatePatch(confirmation.commandeId, cmd);
            await fetchCommandesSilent();
            window.dispatchEvent(new CustomEvent('clickorico:refresh-commandes-pending-count'));
            return true;
          }
        } catch (resyncError) {
          console.error('Resync commande après erreur:', resyncError);
        }
      }
      alert('Erreur lors de la mise à jour. Veuillez réessayer.');
      return false;
    }
  };

  const openRdvStatusConfirmation = (commande, targetStatus, options = {}) => {
    if (!targetStatus) return;

    if (targetStatus === 'TERMINE') {
      setTerminerModal({ open: true, commande });
      return;
    }

    const defaults = getRdvConfirmationMeta(commande, targetStatus);
    const merged = { ...defaults, ...options };

    setConfirmation({
      kind: 'rdv-status',
      commandeId: commande.id,
      targetStatus,
      payload: merged.payload || {},
      afterConfirm: merged.afterConfirm,
      title: merged.title,
      description: merged.description,
      tone: merged.tone || (targetStatus === 'ANNULE' ? 'danger' : 'primary'),
    });
  };

  const handleTerminerSuccess = async () => {
    const id = terminerModal.commande?.id;
    await fetchCommandes();
    if (id && showDetailModal && selectedCommande?.id === id) {
      await refreshCommandeDetail(id);
    }
  };

  const probeCaisseSessionForSelected = useCallback(async () => {
    const cid =
      selectedCommande?.commercantId ||
      selectedCommande?.commercant?.id ||
      commercantId ||
      null;
    if (!cid || !showDetailModal) return;
    setEncaisserSoldeUi((prev) => ({ ...prev, probing: true, error: '' }));
    try {
      const data = await apiService.getCaisseSession(cid);
      setEncaisserSoldeUi((prev) => ({
        ...prev,
        hasSession: Boolean(data?.session?.id),
        probing: false,
      }));
    } catch {
      setEncaisserSoldeUi((prev) => ({
        ...prev,
        hasSession: false,
        probing: false,
      }));
    }
  }, [selectedCommande, commercantId, showDetailModal]);

  useEffect(() => {
    if (!showDetailModal || !selectedCommande || !isRendezVous(selectedCommande)) return;
    if (selectedCommande.statut !== 'TERMINE') return;
    const pay = getRdvPaymentView(selectedCommande);
    if (!(Number(pay?.amountDueLater) > 0)) return;
    probeCaisseSessionForSelected();
  }, [showDetailModal, selectedCommande, probeCaisseSessionForSelected]);

  const handleEncaisserSoldeOnly = async () => {
    if (!selectedCommande) return;
    const cid =
      selectedCommande.commercantId ||
      selectedCommande.commercant?.id ||
      commercantId;
    const pay = getRdvPaymentView(selectedCommande);
    const due = Number(pay?.amountDueLater || 0);
    if (!(due > 0)) return;

    setEncaisserSoldeUi((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const storageKey = `rdv-encaisser-solde:${selectedCommande.id}`;
      let idemKey;
      try {
        idemKey = sessionStorage.getItem(storageKey) || createCaisseIdempotencyKey();
        sessionStorage.setItem(storageKey, idemKey);
      } catch {
        idemKey = createCaisseIdempotencyKey();
      }

      await apiService.encaisserCaisse(
        {
          type: 'RENDEZ_VOUS',
          id: selectedCommande.id,
          methodePaiement: encaisserSoldeUi.methode,
          ...(cid ? { commercantId: cid } : {}),
        },
        { idempotencyKey: idemKey }
      );
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
      await fetchCommandes();
      await refreshCommandeDetail(selectedCommande.id);
    } catch (err) {
      const msg =
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.message ||
        "Impossible d'encaisser le solde";
      setEncaisserSoldeUi((prev) => ({ ...prev, error: msg }));
    } finally {
      setEncaisserSoldeUi((prev) => ({ ...prev, loading: false }));
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'EN_ATTENTE':
        return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMEE':
        return 'bg-blue-100 text-blue-800';
      case 'EN_PREPARATION':
        return 'bg-orange-100 text-orange-800';
      case 'PRETE':
        return 'bg-purple-100 text-purple-800';
      case 'EN_LIVRAISON':
        return 'bg-indigo-100 text-indigo-800';
      case 'LIVREE':
        return 'bg-green-100 text-green-800';
      case 'ANNULEE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'EN_ATTENTE':
        return <Clock className="h-4 w-4" />;
      case 'CONFIRMEE':
      case 'EN_PREPARATION':
      case 'PRETE':
        return <CheckCircle className="h-4 w-4" />;
      case 'EN_LIVRAISON':
        return <Truck className="h-4 w-4" />;
      case 'LIVREE':
        return <CheckCircle className="h-4 w-4" />;
      case 'ANNULEE':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const openCommandeDetail = useCallback(async (commande) => {
    if (!commande?.id) return;
    setIndisponibleMessage('');
    setSelectedCommande(commande);
    setShowDetailModal(true);
    await refreshCommandeDetail(commande.id);
  }, [refreshCommandeDetail]);

  if (loading && commandes.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commandes du commerçant</h1>
          <p className="text-gray-600">Chargement des commandes…</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  if (error && commandes.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commandes du commerçant</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const renderPaiementCell = (commande) => (
    <MesCommandesListPaiementCell commande={commande} isPaiementStripe={isPaiementStripe} />
  );

  const mesCommandesRowContextDeps = {
    isRendezVous,
    getStatusDisplay,
    getTypeBadge,
    getNextStatus,
    getRdvActions,
    updatingStatusId,
    getRdvTotalAmount,
    getCommandeFinancier,
    getListPrincipalAmount,
    formatEuro,
    getNextStatusLabel,
    getRdvConfirmationMeta,
    getRdvActionIcon,
  };

  const buildMesCommandesRowCtx = (commande) =>
    buildMesCommandesRowContext(commande, mesCommandesRowContextDeps);

  const mesCommandesRowHandlers = {
    onOpenDetail: openCommandeDetail,
    onProductStatus: (commande, nextStatus) => openStatusConfirmation(commande, nextStatus),
    onProductCancel: (commande) => openStatusConfirmation(commande, 'ANNULEE'),
    onRdvAction: (commande, status, meta) => openRdvStatusConfirmation(commande, status, meta),
    renderPaiementCell,
  };

  const renderMesCommandesReferenceRow = (commande, { dimmed = false } = {}) => {
    const ctx = buildMesCommandesRowCtx(commande);
    const common = { key: commande.id, commande, ctx, dimmed, ...mesCommandesRowHandlers };
    if (ctx.isRdv) {
      return <MesCommandesRdvReferenceRow {...common} />;
    }
    return <MesCommandesProductReferenceRow {...common} />;
  };

  const orderDetailModalCommonProps = {
    onClose: closeDetailModal,
    isUpdating: updatingStatusId === selectedCommande?.id,
    getNextStatus,
    getStatusDisplay,
    getNextStatusLabel,
    openStatusConfirmation,
    openRefundModal,
    hasStripePaymentIntent,
    getCommandeSoldeRemboursable,
    isCommandePaiementEnLigne,
    isRendezVous,
    reprintLoading,
    onReprintTicket: handleReprintTicket,
    refundLoading,
    factureLoading,
    onDownloadFacture: handleDownloadFacture,
    avoirLoadingId,
    onDownloadAvoir: handleDownloadAvoir,
    livreursDisponibles,
    livreurHistorique,
    selectedLivreurId,
    onSelectedLivreurIdChange: setSelectedLivreurId,
    assigningLivreur,
    onAssignerLivreur: handleAssignerLivreur,
    expeditionCapabilities,
    generatingShippingOrder,
    onOpenGenerateExpeditionConfirmation: openGenerateExpeditionConfirmation,
    expeditionLabelUi,
    expeditionLabelPolling,
    onRefreshExpeditionLabelStatus: refreshExpeditionLabelStatus,
    onDownloadExpeditionLabel: handleDownloadExpeditionLabel,
    merchantHandoffSubmitting,
    onOpenExpeditionHandoffConfirmation: openExpeditionHandoffConfirmation,
    expeditionTrackingSyncing,
    onSyncExpeditionTracking: handleSyncExpeditionTracking,
    poidsComplementUrl,
    poidsDrafts,
    onPoidsDraftsChange: setPoidsDrafts,
    indisponibleMessage,
    markingIndisponible,
    onOpenLigneIndisponibleConfirmation: openLigneIndisponibleConfirmation,
    statutsLigneIndisponible: STATUTS_LIGNE_INDISPONIBLE,
    parseSavReference,
    formatDateTime,
    poidsError,
    poidsMessage,
    savingPoids,
    onSavePoidsAjustement: handleSavePoidsAjustement,
  };

  const renderOrderDetailModal = () => {
    if (!showDetailModal || !selectedCommande || isRendezVous(selectedCommande)) return null;
    return (
      <MesCommandesOrderDetailModal
        commande={selectedCommande}
        {...orderDetailModalCommonProps}
      />
    );
  };

  const renderRdvDetailModal = () => {
    if (!showDetailModal || !selectedCommande || !isRendezVous(selectedCommande)) return null;
    return (
      <MesCommandesRdvDetailModal
        commande={selectedCommande}
        statusDisplay={getStatusDisplay(selectedCommande)}
        isUpdating={updatingStatusId === selectedCommande.id}
        rdvActions={getRdvActions(selectedCommande.statut)}
        encaisserSoldeUi={{
          ...encaisserSoldeUi,
          onMethodeChange: (methode) =>
            setEncaisserSoldeUi((prev) => ({ ...prev, methode })),
        }}
        onClose={closeDetailModal}
        onRdvAction={(commande, status, meta) =>
          openRdvStatusConfirmation(commande, status, meta)
        }
        onOpenRefund={openRefundModal}
        onEncaisserSolde={handleEncaisserSoldeOnly}
        getRdvActionButtonClass={getRdvActionButtonClass}
        getRdvActionIcon={getRdvActionIcon}
        getRdvConfirmationMeta={getRdvConfirmationMeta}
        refundLoading={refundLoading}
      />
    );
  };


  const renderRefundModal = () => {
    if (!showRefundModal || !selectedCommande) return null;
    const isRdv = isRendezVous(selectedCommande);
    const solde = isRdv
      ? getRdvSoldeRemboursable(selectedCommande)
      : getCommandeSoldeRemboursable(selectedCommande);
    const tenders = isRdv
      ? null
      : buildTenderBreakdown(selectedCommande?.paiements || [], {
          modePaiement: selectedCommande?.modePaiement,
        });
    const giftBlocksPartial = !isRdv && tenders?.hasGift;
    const lignesDispo = isRdv
      ? []
      : (selectedCommande.produits || []).filter(
          (lp) => getLigneRemainingQty(lp, selectedCommande) > 0
        );
    const selectedArticlesTotal = lignesDispo.reduce((s, lp) => {
      const qty = refundLineQty[lp.id] || 0;
      if (qty <= 0) return s;
      return s + getLigneUnitPrice(lp) * qty;
    }, 0);
    const freeVatUi = analyzeFreeAmountVatUi(selectedCommande);
    const refundTabs = isRdv
      ? [
          { key: 'COMPLET', label: 'Total' },
          { key: 'LIBRE', label: 'Partiel' },
        ]
      : giftBlocksPartial
        ? [{ key: 'COMPLET', label: 'Total' }]
        : [
            { key: 'ARTICLES', label: 'Par article' },
            ...(freeVatUi.freeAmountAllowed
              ? [{ key: 'LIBRE', label: 'Montant libre' }]
              : []),
            { key: 'COMPLET', label: 'Complet' },
          ];
    return (
      <Modal
        isOpen={showRefundModal}
        onClose={() => !refundLoading && setShowRefundModal(false)}
        title={`Rembourser — ${selectedCommande.numero || selectedCommande.id.slice(-8)}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Solde remboursable : <strong>{formatEuro(solde)}</strong>.
            {isRdv
              ? ' Le remboursement Stripe ne change pas le statut du rendez-vous (annuler reste une action séparée).'
              : giftBlocksPartial
                ? ' Un remboursement total annule la commande par défaut.'
                : ' Sélectionnez les articles pour un suivi précis (remboursement partiel Stripe existant).'}
          </p>
          {tenders?.lines?.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 space-y-1">
              <p className="text-xs font-semibold text-gray-500 uppercase">Répartition</p>
              {tenders.lines.map((l) => (
                <p key={l.methode} className="text-sm flex justify-between gap-4">
                  <span>{l.label}</span>
                  <span className="font-medium tabular-nums">{formatEuro(l.amount)}</span>
                </p>
              ))}
              {tenders.isMixed && refundType === 'COMPLET' ? (
                <p className="text-xs text-gray-600 pt-1">
                  Le remboursement sera réparti automatiquement sur les moyens de paiement d’origine.
                </p>
              ) : null}
            </div>
          )}
          <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
            {refundTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setRefundType(tab.key)}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg ${
                  refundType === tab.key ? 'bg-white text-orange-700 shadow-sm' : 'text-slate-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {giftBlocksPartial && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Remboursement partiel indisponible : cette commande inclut une carte cadeau. Seul le
              remboursement total (répartition Gift + Stripe) est supporté.
            </p>
          )}
          {!isRdv && !giftBlocksPartial && !freeVatUi.freeAmountAllowed && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {freeVatUi.isMultiVat
                ? 'Cette commande comporte plusieurs taux de TVA. Pour un remboursement partiel, sélectionnez les articles concernés.'
                : 'Montant libre indisponible : ventilation TVA incomplète. Utilisez « Par article » ou « Complet ».'}
            </p>
          )}
          {refundType === 'ARTICLES' && !giftBlocksPartial && !isRdv && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {lignesDispo.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucun article remboursable</p>
              ) : (
                lignesDispo.map((lp) => {
                  const qtySelected = refundLineQty[lp.id] || 0;
                  const isActive = qtySelected > 0;
                  const remaining = getLigneRemainingQty(lp, selectedCommande);
                  const unitPrice = getLigneUnitPrice(lp);
                  return (
                    <div
                      key={lp.id}
                      className={`rounded-xl px-4 py-3 border-2 ${
                        isActive ? 'border-orange-400 bg-orange-50' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            setRefundLineQty((prev) => ({
                              ...prev,
                              [lp.id]: isActive ? 0 : remaining,
                            }))
                          }
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                            isActive ? 'bg-orange-500 border-orange-500' : 'border-slate-300'
                          }`}
                        >
                          {isActive && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{lp.produit?.nom || 'Produit'}</p>
                          <p className="text-xs text-slate-400">{formatEuro(unitPrice)} / unité</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold tabular-nums">
                            {formatEuro((unitPrice * (qtySelected || remaining)))}
                          </p>
                          {isActive && remaining > 1 && (
                            <div className="flex items-center gap-2 mt-1 justify-end">
                              <button
                                type="button"
                                onClick={() =>
                                  setRefundLineQty((prev) => ({
                                    ...prev,
                                    [lp.id]: Math.max(1, qtySelected - 1),
                                  }))
                                }
                                className="w-7 h-7 rounded-lg bg-orange-200 flex items-center justify-center"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="text-sm font-medium w-6 text-center">{qtySelected}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  setRefundLineQty((prev) => ({
                                    ...prev,
                                    [lp.id]: Math.min(remaining, qtySelected + 1),
                                  }))
                                }
                                className="w-7 h-7 rounded-lg bg-orange-200 flex items-center justify-center"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {selectedArticlesTotal > 0 && (
                <p className="text-sm font-semibold text-orange-800 text-right pt-1">
                  Total sélection : {formatEuro(selectedArticlesTotal)}
                </p>
              )}
            </div>
          )}
          {refundType === 'LIBRE' && !giftBlocksPartial && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€)</label>
              <input
                type="number"
                min="0.01"
                max={solde}
                step="0.01"
                value={refundMontant}
                onChange={(e) => setRefundMontant(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder={solde.toFixed(2)}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motif (optionnel)</label>
            <textarea
              value={refundMotif}
              onChange={(e) => setRefundMotif(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              rows={2}
              placeholder="Ex. article en rupture"
            />
          </div>
          {refundError && <p className="text-sm text-red-600">{refundError}</p>}
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={() => setShowRefundModal(false)}
              disabled={refundLoading}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={openRefundConfirm}
              disabled={refundLoading}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {tenders?.isMixed || tenders?.hasGift
                ? 'Effectuer le remboursement total'
                : 'Effectuer le remboursement'}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  const renderRefundConfirmDialog = () => (
    <ConfirmDialog
      isOpen={showRefundConfirm}
      title={
        !isRendezVous(selectedCommande) && hasGiftTender(selectedCommande)
          ? 'Confirmer le remboursement (répartition d’origine) ?'
          : 'Confirmer le remboursement Stripe ?'
      }
      description={
        !isRendezVous(selectedCommande) && hasGiftTender(selectedCommande)
          ? 'Le montant carte bancaire sera remboursé via Stripe ; le montant carte cadeau sera recrédité sur la carte. Action irréversible.'
          : refundType === 'ARTICLES'
            ? 'Un remboursement Stripe (montant libre) sera effectué pour les articles sélectionnés.'
            : 'Cette action déclenchera un remboursement réel vers le client et ne pourra pas être annulée.'
      }
      confirmLabel="Confirmer le remboursement"
      cancelLabel="Annuler"
      tone="danger"
      confirmDisabled={refundLoading}
      onConfirm={handleRembourserCommande}
      onCancel={() => !refundLoading && setShowRefundConfirm(false)}
    />
  );

  const renderDetailModal = () => {
    if (!showDetailModal || !selectedCommande) return null;
    return isRendezVous(selectedCommande) ? renderRdvDetailModal() : renderOrderDetailModal();
  };

  return (
    <>
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}
      <div className="space-y-5 pb-4 rounded-2xl bg-gradient-to-b from-slate-100/90 via-slate-50/40 to-transparent p-4 sm:p-5 -mx-1 sm:mx-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes Commandes</h1>
          <p className="text-sm text-gray-500 mt-0.5">{mesCommandesSubtitleForCapabilities(mesCommandesCapabilities)}</p>
        </div>

      <MesCommandesMaquetteToolbar
        activeId={quickViewFilter}
        onChange={setQuickViewFilter}
        capabilities={mesCommandesCapabilities}
        counts={{
          [MES_COMMANDES_QUICK_VIEW.ALL]: commandes.length,
          [MES_COMMANDES_QUICK_VIEW.COMMANDES]: commandes.filter((c) => !isRendezVous(c)).length,
          [MES_COMMANDES_QUICK_VIEW.RDV]: commandes.filter((c) => isRendezVous(c)).length,
          [MES_COMMANDES_QUICK_VIEW.ACTIONS]: commandes.filter((c) => isMesCommandesActionRequise(c)).length,
        }}
      />

      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3 md:space-y-0 md:flex md:flex-wrap md:items-end md:gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
            Recherche
          </label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Numéro, client, téléphone, produit..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
            Statut
          </label>
          <select
            value={filters.statut}
            onChange={(e) => handleFilterChange('statut', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Tous</option>
            <option value="EN_ATTENTE">En attente</option>
              <option value="CONFIRMEE">Commandes confirmées</option>
            <option value="EN_PREPARATION">En préparation</option>
            <option value="PRETE">Prête</option>
            <option value="EN_LIVRAISON">En livraison</option>
            <option value="LIVREE">Livrée</option>
            <option value="ANNULEE">Annulée</option>
              <option value="CONFIRME">RDV confirmés</option>
              <option value="TERMINE">RDV terminés</option>
              <option value="ABSENT">RDV absents</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
            Du
          </label>
          <input
            type="date"
            value={filters.dateMin}
            onChange={(e) => handleFilterChange('dateMin', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
            Au
          </label>
          <input
            type="date"
            value={filters.dateMax}
            onChange={(e) => handleFilterChange('dateMax', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          type="button"
          onClick={resetFilters}
          className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          Réinitialiser
        </button>
      </div>

        {!loading ? (
          <div className="space-y-6 mt-2">
            {/* Grouper les commandes par statut */}
            {(() => {
              const visibleCommandes = filterCommandesByQuickView(commandes, quickViewFilter);
              const showProductSections = shouldShowMesCommandesProductBlock(
                quickViewFilter,
                mesCommandesCapabilities,
              );
              const showRdvSections = shouldShowMesCommandesRdvBlock(
                quickViewFilter,
                mesCommandesCapabilities,
              );

              // Commandes en cours
              const commandesEnCours = visibleCommandes.filter((cmd) => isMerchantCommandeEnCours(cmd));
              const produitsEnCours = commandesEnCours.filter((cmd) => !isRendezVous(cmd));
              const rdvEnCoursAll = commandesEnCours.filter((cmd) => isRendezVous(cmd));
              const rdvEnCoursDisplayed = resolveMesCommandesRdvEnCoursDisplayed(
                rdvEnCoursAll,
                rdvEnCoursListExpanded,
              );
              const rdvEnCoursFooter = buildMesCommandesRdvEnCoursFooterState(
                rdvEnCoursAll.length,
                rdvEnCoursDisplayed.length,
                rdvEnCoursListExpanded,
              );
              
              // Commandes terminées
              const commandesTerminees = visibleCommandes.filter((cmd) => isMerchantCommandeTerminee(cmd));
              
              // Commandes annulées
              const commandesAnnulees = visibleCommandes.filter(cmd => {
                const statut = cmd.statut;
                return statut === 'ANNULEE' || statut === 'ANNULE';
              });

              const termineesProduits = commandesTerminees.filter((c) => !isRendezVous(c));
              const termineesRdv = commandesTerminees.filter((c) => isRendezVous(c));
              const annuleesProduits = commandesAnnulees.filter((c) => !isRendezVous(c));
              const annuleesRdv = commandesAnnulees.filter((c) => isRendezVous(c));

              return (
                <>
                  {showProductSections ? (
                    <MesCommandesMaquetteBlock
                      title="Commandes en cours"
                      count={produitsEnCours.length}
                      icon={Clock}
                    >
                      <div className="overflow-x-auto">
                        <table className={MES_COMMANDES_MAQUETTE_TABLE_CLASS}>
                          <MesCommandesProductTableHead />
                          <tbody className="bg-white">
                            {produitsEnCours.length > 0 ? (
                              produitsEnCours.map((c) => renderMesCommandesReferenceRow(c))
                            ) : (
                              <MesCommandesMaquetteEmptyRow
                                colSpan={8}
                                message="Aucune commande produit en cours pour cette sélection."
                              />
                            )}
                          </tbody>
                        </table>
                      </div>
                    </MesCommandesMaquetteBlock>
                  ) : null}

                  {showRdvSections ? (
                    <MesCommandesMaquetteBlock
                      title={MES_COMMANDES_PRESTATIONS_RDV_BLOCK_TITLE}
                      showTitleCount={false}
                      icon={Calendar}
                      headerRight={
                        <Link
                          to={MES_COMMANDES_PLANNING_PATH}
                          className="text-sm font-semibold text-white hover:text-white/90 underline underline-offset-2 whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {MES_COMMANDES_PLANNING_LINK_LABEL}
                        </Link>
                      }
                    >
                      <div className="overflow-x-auto">
                        <table className={MES_COMMANDES_MAQUETTE_TABLE_CLASS}>
                          <MesCommandesRdvTableHead />
                          <tbody className="bg-white">
                            {rdvEnCoursDisplayed.length > 0 ? (
                              rdvEnCoursDisplayed.map((c) => renderMesCommandesReferenceRow(c))
                            ) : (
                              <MesCommandesMaquetteEmptyRow
                                colSpan={9}
                                message="Aucune prestation ou RDV planifié pour cette sélection."
                              />
                            )}
                          </tbody>
                        </table>
                      </div>
                      {rdvEnCoursFooter.showFooter ? (
                        <div className="px-5 py-3 border-t border-gray-100 bg-blue-50/50 text-sm text-gray-700">
                          {rdvEnCoursFooter.summaryText}
                          {rdvEnCoursFooter.showExpand && rdvEnCoursFooter.expandLabel ? (
                            <>
                              {'. '}
                              <button
                                type="button"
                                className="font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
                                onClick={() => setRdvEnCoursListExpanded(true)}
                              >
                                {rdvEnCoursFooter.expandLabel}
                              </button>
                            </>
                          ) : null}
                          {rdvEnCoursFooter.showReduce ? (
                            <>
                              {'. '}
                              <button
                                type="button"
                                className="font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
                                onClick={() => setRdvEnCoursListExpanded(false)}
                              >
                                {MES_COMMANDES_RDV_REDUCE_LIST_LABEL}
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </MesCommandesMaquetteBlock>
                  ) : null}

                  {/* Section Commandes terminées */}
                  {commandesTerminees.length > 0 && (
                    <div className="bg-white rounded-lg shadow-lg border-2 border-green-200">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-3 rounded-t-lg cursor-pointer hover:from-green-600 hover:to-green-700 transition-colors"
                        onClick={() => setSectionsExpanded(prev => ({ ...prev, terminees: !prev.terminees }))}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CheckCircle className="h-5 w-5 text-white mr-2" />
                            <h2 className="text-lg font-bold text-white">Commandes terminées ({commandesTerminees.length})</h2>
                          </div>
                          <div className="flex items-center">
                            {sectionsExpanded.terminees ? (
                              <ChevronUp className="h-5 w-5 text-white" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                      {sectionsExpanded.terminees && (
                        <div className="space-y-4 p-2">
                          {termineesProduits.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-100">
                              <table className={MES_COMMANDES_MAQUETTE_TABLE_CLASS}>
                                <MesCommandesProductTableHead />
                                <tbody className="bg-white">
                                  {termineesProduits.map((c) =>
                                    renderMesCommandesReferenceRow(c, { dimmed: true }),
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                          {termineesRdv.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-100">
                              <table className={MES_COMMANDES_MAQUETTE_TABLE_CLASS}>
                                <MesCommandesRdvTableHead />
                                <tbody className="bg-white">
                                  {termineesRdv.map((c) => renderMesCommandesReferenceRow(c, { dimmed: true }))}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Section Commandes annulées */}
                  {commandesAnnulees.length > 0 && (
                    <div className="bg-white rounded-lg shadow-lg border-2 border-red-200">
                      <div 
                        className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-3 rounded-t-lg cursor-pointer hover:from-red-600 hover:to-red-700 transition-colors"
                        onClick={() => setSectionsExpanded(prev => ({ ...prev, annulees: !prev.annulees }))}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <XCircle className="h-5 w-5 text-white mr-2" />
                            <h2 className="text-lg font-bold text-white">Commandes annulées ({commandesAnnulees.length})</h2>
                          </div>
                          <div className="flex items-center">
                            {sectionsExpanded.annulees ? (
                              <ChevronUp className="h-5 w-5 text-white" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                      {sectionsExpanded.annulees && (
                        <div className="space-y-4 p-2">
                          {annuleesProduits.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-100">
                              <table className={MES_COMMANDES_MAQUETTE_TABLE_CLASS}>
                                <MesCommandesProductTableHead />
                                <tbody className="bg-white">
                                  {annuleesProduits.map((c) =>
                                    renderMesCommandesReferenceRow(c, { dimmed: true }),
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                          {annuleesRdv.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-100">
                              <table className={MES_COMMANDES_MAQUETTE_TABLE_CLASS}>
                                <MesCommandesRdvTableHead />
                                <tbody className="bg-white">
                                  {annuleesRdv.map((c) => renderMesCommandesReferenceRow(c, { dimmed: true }))}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
              <p className="mt-1 text-sm text-gray-500">
                Il n&apos;y a pas encore de commandes pour ce commerçant.
              </p>
            </div>
          )}

        {renderDetailModal()}
        {renderRefundModal()}
        {renderRefundConfirmDialog()}

        <RdvTerminerModal
          open={terminerModal.open}
          commande={terminerModal.commande}
          commercantId={
            terminerModal.commande?.commercantId ||
            terminerModal.commande?.commercant?.id ||
            commercantId
          }
          onClose={() => setTerminerModal({ open: false, commande: null })}
          onSuccess={handleTerminerSuccess}
          formatEuro={formatEuro}
        />
      </div>

      <ConfirmDialog
        isOpen={!!confirmation}
        onCancel={() => {
          if (markingIndisponible) return;
          setConfirmation(null);
        }}
        onConfirm={handleConfirmationConfirm}
        title={confirmation?.title}
        description={confirmation?.description}
        tone={confirmation?.tone}
        confirmLabel={
          confirmation?.kind === 'ligne-indisponible' && markingIndisponible
            ? 'Marquage…'
            : confirmation?.confirmLabel
        }
        confirmDisabled={
          (!!confirmation?.requireAssign && !selectedLivreurId) ||
          (confirmation?.kind === 'ligne-indisponible' && markingIndisponible) ||
          (confirmation?.kind === 'expedition-shipping-order' && generatingShippingOrder) ||
          (confirmation?.kind === 'expedition-handoff' && merchantHandoffSubmitting)
        }
      >
        {confirmation?.showAssignSelect ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-800">Assigner un livreur</p>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={selectedLivreurId}
              onChange={(e) => setSelectedLivreurId(e.target.value)}
            >
              <option value="">Sélectionner un livreur...</option>
              {livreursDisponibles.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.nom} {l.prenom ? `(${l.prenom})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              Choisissez un livreur puis confirmez pour passer la commande en livraison.
            </p>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
};

export default CommercantCommandes;


